import { Component, ViewChild, OnInit, inject, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, FormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { MatMenuModule } from '@angular/material/menu';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { UnitDetailPage } from 'src/pages/leasing/unit-detail/unit-detail';
import { ScreeningsTabComponent } from 'src/pages/leasing/components/screenings-tab/screenings-tab.component';

import { LeasesContentComponent } from 'src/shared/components/leases-content/leases-content.component';

import { DemoDataService } from 'src/shared/services/demo-data.service';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { RentalUnitsService, RentalUnit, UnitState } from 'src/shared/services/rental-units.service';
import { UrlStateService } from 'src/shared/services/url-state.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

// Precomputed per-unit view model for the pipeline list — keeps the template
// free of function calls (CLAUDE.md rule) for split-unit display state/occupancy.
export interface UnitRowView {
  unit: RentalUnit;
  isByRoom: boolean;
  displayState: string;
  // Colored from `displayState`, not the stored `unit.state` — for a split unit
  // the two differ (e.g. derived "Partially occupied" vs stored "Occupied").
  displayStateColor: string;
  occupancyLabel: string;
  tagColor: string;
}

// Real accounts have no leasing backend yet — they get a bare unit list built
// from their addresses (bed/bath only; rent/state/status are dashes).
export interface RealUnitView {
  id: number;
  name: string;
  bedBath: string;
  color: string;
}

export interface StateFilterOption {
  state: UnitState;
  count: number;
  color: string;
}

@Component({
  selector: 'units-content',
  templateUrl: './units-content.component.html',
  styleUrls: ['./units-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    IonicModule,
    MatMenuModule,
    TidyUiComponentsModule,
    SharedModule,
    ScreeningsTabComponent,
    LeasesContentComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UnitsContentComponent implements OnInit {

  // Same family as the tidy-tag palette — gives each demo unit a stable house
  // color (real addresses use their saved `color` instead).
  private static readonly DEMO_TAG_COLORS = [
    '#72CB9B', '#EFCE60', '#F1A76E', '#E7796D', '#9C90E9',
    '#679BF8', '#81C1DD', '#9FC65B', '#D87AB8', '#8790A0',
  ];

  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private client = inject(Client);
  private demoData = inject(DemoDataService);
  private featureGate = inject(FeatureGateService);
  private unitsService = inject(RentalUnitsService);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);
  private route = inject(ActivatedRoute);
  private urlStateService = inject(UrlStateService);

  readonly windowService = inject(WindowService);

  // Leasing splits into Properties (the unit pipeline), Screenings, and Leases
  // (the same leases-content the Finance page embeds), using the Contacts
  // paradigm: tabs live in the page header, the selected tab's own header sits
  // below it with that section's controls.
  readonly leasingTabs = [
    { value: 'properties', label: 'Properties', icon: 'assets/svg/home-outline.svg' },
    { value: 'screenings', label: 'Screenings', icon: 'assets/svg/shield-checkmark-outline.svg' },
    { value: 'leases', label: 'Leases', icon: 'assets/svg/document-text-outline.svg' },
  ];
  selectedTab = 'properties';

  form: UntypedFormGroup;
  units: RentalUnit[] = [];
  unitViews: UnitRowView[] = [];
  shownUnitViews: UnitRowView[] = [];
  realViews: RealUnitView[] = [];
  shownRealViews: RealUnitView[] = [];
  headers: string[] = [];
  shownRows: any[] = [];
  loaded = false;
  isRealMode = false;
  errorMessage: string;
  emptyStateMessage = '';

  // Single-select status filter — no stacking, no "All" option; the active
  // chip (or re-picking the same status) clears it.
  selectedState: UnitState | null = null;
  activeFilter: StateFilterOption | null = null;
  filterOptions: StateFilterOption[] = [];

  @ViewChild('tidyTable') tidyTable: any;
  private panelClosedSubscription: Subscription;

  constructor() {
    this.form = this.fb.group({ search: [''] });
    this.form.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.applyFilters());
  }

  async ngOnInit() {
    // `tab` is the Marketing page's own param, so the sub-tab gets its own key.
    this.route.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      const tab = params['leasingTab'];
      if (tab && this.leasingTabs.some(t => t.value === tab)) {
        this.selectedTab = tab;
        this.cdr.markForCheck();
      }
    });
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load units';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  selectTab = (tab: string) => {
    this.selectedTab = tab;
    this.urlStateService.updateQueryParams({ leasingTab: tab });
    this.cdr.markForCheck();
  };

  async loadPage() {
    this.headers = this.buildHeaders();
    // Must match what RentalUnitsService gates on, not just the demo-account
    // flag — otherwise a preview session (which the service serves mock units
    // for) takes the real-account branch and renders bare address stubs, while
    // the sibling Leases/Screenings tabs show full mock data.
    this.isRealMode = !this.demoData.shouldServeTemplates();
    if (this.isRealMode) {
      const addresses = await this.client.getAddresses();
      this.realViews = (addresses || []).map(address => ({
        id: address.id,
        name: this.client.buildAddressName(address),
        bedBath: this.bedBathLabel(address),
        color: address.color || '#000000',
      }));
    } else {
      this.units = await this.unitsService.getUnits();
      this.unitViews = this.buildUnitViews(this.units);
      this.buildFilterOptions();
    }
    this.applyFilters();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  buildUnitViews(units: RentalUnit[]): UnitRowView[] {
    return units.map(unit => {
      const isByRoom = this.unitsService.isByRoom(unit);
      const displayState = isByRoom ? this.unitsService.derivedState(unit) : unit.state;
      return {
        unit,
        isByRoom,
        displayState,
        displayStateColor: this.unitsService.stateColor(displayState),
        occupancyLabel: isByRoom ? this.unitsService.occupancySummary(unit).label : '',
        tagColor: UnitsContentComponent.DEMO_TAG_COLORS[unit.id % UnitsContentComponent.DEMO_TAG_COLORS.length],
      };
    });
  }

  buildHeaders(): string[] {
    return ['Unit', 'Beds/Baths', 'Rent', 'State', 'Status', 'last-column'];
  }

  buildFilterOptions(): void {
    this.filterOptions = this.unitsService.states.map(state => ({
      state,
      count: this.units.filter(u => this.unitsService.matchesStateFilter(u, state)).length,
      color: this.unitsService.stateColor(state),
    }));
  }

  buildRows(views: UnitRowView[]): any[] {
    return views.map(({ unit, isByRoom, displayState, occupancyLabel, tagColor }) => ({
      id: unit.id,
      'Unit': { value: `${unit.property_name} · ${unit.unit}`, color: tagColor, addressId: unit.address_id },
      'Beds/Baths': { value: `${unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms  } bd`} / ${unit.bathrooms} ba` },
      'Rent': { value: `$${unit.market_rent.toLocaleString('en-US')}` },
      'State': { value: this.stateCellHtml(displayState) },
      'Status': { value: isByRoom && occupancyLabel ? occupancyLabel : unit.status_summary },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  // Same colored dot the filter menu uses, inline before the state word (the
  // table renders cell values via innerHTML, so this survives sanitization).
  private stateCellHtml(displayState: string): string {
    const color = this.unitsService.stateColor(displayState);
    return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background-color:${color};margin-right:6px;vertical-align:middle;"></span>${displayState}`;
  }

  buildRealRows(views: RealUnitView[]): any[] {
    return views.map(view => ({
      id: view.id,
      'Unit': { value: view.name, color: view.color, addressId: view.id },
      'Beds/Baths': { value: view.bedBath },
      'Rent': { value: '-' },
      'State': { value: '-' },
      'Status': { value: '-' },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  bedBathLabel(address: any): string {
    const beds = address?.bed_count ?? null;
    const baths = address?.bath_count ?? null;
    if (beds === null && baths === null) { return '-'; }
    return `${beds ?? '-'} bd / ${baths ?? '-'} ba`;
  }

  selectFilter(state: UnitState): void {
    this.selectedState = this.selectedState === state ? null : state;
    this.activeFilter = this.filterOptions.find(option => option.state === this.selectedState) || null;
    this.applyFilters();
  }

  clearFilter(): void {
    this.selectedState = null;
    this.activeFilter = null;
    this.applyFilters();
  }

  applyFilters(): void {
    const search = (this.form.get('search')?.value || '').toLowerCase();
    const searching = search.length >= 2;
    if (this.isRealMode) {
      this.shownRealViews = searching
        ? this.realViews.filter(view => view.name.toLowerCase().includes(search))
        : [...this.realViews];
      this.shownRows = this.buildRealRows(this.shownRealViews);
      this.emptyStateMessage = searching ? 'No units match your search.' : 'No units yet.';
    } else {
      let views = this.selectedState
        ? this.unitViews.filter(view => this.unitsService.matchesStateFilter(view.unit, this.selectedState))
        : [...this.unitViews];
      if (searching) {
        views = views.filter(view =>
          `${view.unit.property_name} ${view.unit.unit}`.toLowerCase().includes(search) ||
          view.unit.status_summary.toLowerCase().includes(search)
        );
      }
      this.shownUnitViews = views;
      this.shownRows = this.buildRows(views);
      this.emptyStateMessage = 'No units match this filter.';
    }
    this.cdr.markForCheck();
  }

  getUnit(id: number): RentalUnit | undefined {
    return this.units.find(u => u.id === id);
  }

  sortChanged(sort: any): void {
    if (!sort.active || !sort.direction) { return; }
    this.shownRows = [...this.shownRows].sort((a, b) => {
      const strA = String(a[sort.active]?.value || '').toLowerCase();
      const strB = String(b[sort.active]?.value || '').toLowerCase();
      if (strA < strB) { return sort.direction === 'asc' ? -1 : 1; }
      if (strA > strB) { return sort.direction === 'asc' ? 1 : -1; }
      return 0;
    });
  }

  onTableColumnClick(event: { column: string; element: any }): void {
    if (this.isRealMode) {
      this.featureGate.gate();
      return;
    }
    const unit = this.getUnit(event.element.id);
    if (unit) { this.goToUnitDetail(unit); }
  }

  onRealRowClick(): void {
    this.featureGate.gate();
  }

  goToUnitDetail(unit: RentalUnit): void {
    this.goToPage('unit-detail', { unit }, UnitDetailPage);
  }

  async goToPage(url: string, params: any, component: any): Promise<void> {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => {
        await this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
